import { useRef, useMemo } from 'react';
import clsx from 'clsx';
import { useActiveBundle } from '@/context/active-bundle';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { Copilot, GlobalCopilotConfiguration } from '@/features/copilot';
import { useAuth } from '@/services/auth';
import { FeatureFlag, hasFeatureFlag } from '@/services/auth/feature-flags';
import { AccountState } from '@/services/auth/types';
import { AppBundle } from '@/utils/app-bundles';
import { Box, InlineStack, Breadcrumbs, Link, Text, useSideNavState } from '@customerio/pluma-components/react';
import { newTheme } from '@customerio/pluma-components/css';
import { PersonDropdown } from './components/person-dropdown';
import { SampleWorkspaceBadge } from './components/sample-workspace';
import * as styles from './index.css';
import { NotificationsDropdown } from './components/notifications-dropdown';
import { NotificationsDrawer } from './components/notifications-drawer';
import { SupportChat, useSupportChat } from '@/features/support';
import { HelpButton } from '@/features/help-sidebar';
import { CommandDialogButton } from './components/command-dialog-button';
import { useBreadcrumbs } from '@/services/breadcrumbs';
import type { NavigationContextHistoryState } from '@/services/router/navigation-history';
import { useNavigationHistory } from '@/services/router/navigation-history';
import { WorkspaceHealthNotification } from './components/workspace-health-notification';
import { useLocation } from 'react-router-dom';
import { MotionConfig, m } from 'motion/react';
import { WorkspaceSelector } from './workspace-selector';
import type { TargetTransitionOptions } from '@/services/router/safe-navigate';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import { shouldProcessLinkClick, workspaceRootUrl } from '@/utils/navigation';
import { useIsSettings } from '@/services/router/use-is-settings';
import { MODERATE_01_STANDARD_PRODUCTIVE_TRANSITION } from '@/utils/motion';

const SPACER_WIDTH = Number.parseFloat(newTheme.tokensMeta['space-300'].$value);
const SEPARATOR_WIDTH = 18;

// When the side nav is expanded, this element acts as a spacer between the
// workspace selector and breadcrumbs. When collapsed (and breadcrumbs exist),
// it shows a "/" separator. The collapsed variant uses `custom` to determine
// whether breadcrumbs are present — if not, the element shrinks to zero.
const spacerSeparatorVariants = {
  expanded: {
    width: SPACER_WIDTH,
    opacity: 0,
    // Typically we'd use enter/exit easings for appearing/disappearing
    // content, but we want this transition to be in sync with the workspace button itself
    transition: MODERATE_01_STANDARD_PRODUCTIVE_TRANSITION,
  },
  collapsed: (hasBreadcrumbs: boolean) => ({
    width: hasBreadcrumbs ? SEPARATOR_WIDTH : 0,
    opacity: hasBreadcrumbs ? 1 : 0,
    transition: MODERATE_01_STANDARD_PRODUCTIVE_TRANSITION,
  }),
};

function ExitSettingsLink() {
  const safeNavigate = useSafeNavigate();
  const activeApp = useActiveBundle();
  const { history } = useNavigationHistory();
  const location = useLocation();
  const workspace = useCurrentWorkspace();
  const auth = useAuth();

  const exitSettingsRouteInfo = useRef<NavigationContextHistoryState | null>(null);
  const lastHistoryEntry = history[history.length - 1];

  if (lastHistoryEntry?.url !== `${location.pathname}${location.search}` && exitSettingsRouteInfo.current == null) {
    exitSettingsRouteInfo.current = lastHistoryEntry;
  }

  exitSettingsRouteInfo.current ??= {
    url: auth.account?.is_masq && workspace != null ? workspaceRootUrl(workspace.id) : '/workspaces/last/journeys',
    app: activeApp,
  };

  const exitSettingsUrl = exitSettingsRouteInfo.current?.url ?? '/';
  const onExitSettingsClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!shouldProcessLinkClick(event)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const targetUrl = event.currentTarget.getAttribute('href') ?? '';

    safeNavigate(targetUrl, {
      shouldUnrenderOutlet: activeApp !== exitSettingsRouteInfo.current?.app,
    });
  };

  return (
    <Link
      href={exitSettingsUrl}
      onClick={onExitSettingsClick as any}
      className={styles.exitLink}
      icon="chevron-left"
      variant="secondary"
      truncate={true}
      flexShrink="0"
    >
      Exit Settings
    </Link>
  );
}

export function TopNavigation(props: { isCollapsible?: boolean }) {
  const { isCollapsible = true } = props;

  const supportChat = useSupportChat();
  const activeApp = useActiveBundle();
  const auth = useAuth();
  const sideNavState = useSideNavState();
  const { isAnySettingsPage } = useIsSettings();
  const safeNavigate = useSafeNavigate();

  const isSideNavCollapsed = isCollapsible && sideNavState != null && sideNavState.isCollapsed === true;

  const hasWorkspaceAccess =
    auth.account?.account_state !== AccountState.Suspended && auth.account?.account_state !== AccountState.Canceled;

  const showExitSettingsLink =
    auth.account.is_masq ?? false ? !auth.user.noMasqAccess && !auth.user.masqLevelNotSet : hasWorkspaceAccess;

  const { breadcrumbs } = useBreadcrumbs();

  const plumaBreadcrumbs = useMemo(
    () =>
      breadcrumbs.map((crumb) => ({
        text: crumb.text ?? crumb.name,
        href: crumb.href,
        linkClassName: crumb.className,
        isTruncated: crumb.isTruncated,
      })),
    [breadcrumbs],
  );

  const onLinkClick = (event: React.MouseEvent<HTMLAnchorElement>, options?: TargetTransitionOptions) => {
    if (!shouldProcessLinkClick(event)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    safeNavigate(event.currentTarget.getAttribute('href') ?? '', options);
  };

  const hasBreadcrumbs = plumaBreadcrumbs.length > 1;
  const variantName = isSideNavCollapsed ? 'collapsed' : 'expanded';

  return (
    <MotionConfig reducedMotion="user">
      <Box
        className={clsx(
          styles.topNavigation,
          isCollapsible && sideNavState != null && sideNavState.isCollapsed === true
            ? styles.topNavigationCollapsed
            : null,
          styles.topNavigationWithCommandDialogButton,
        )}
      >
        <InlineStack alignBlock="center" alignInline="start" gap="100" whiteSpace="nowrap">
          <InlineStack
            alignBlock="center"
            alignInline="start"
            gap={isAnySettingsPage && showExitSettingsLink ? '200' : undefined}
          >
            {isAnySettingsPage ? (
              showExitSettingsLink ? (
                <ExitSettingsLink />
              ) : null
            ) : (
              <>
                <WorkspaceSelector onLinkClick={onLinkClick} />
                {/* key={hasBreadcrumbs} remounts on breadcrumb changes (e.g. during
                    initial mount settling or navigation), so initial={false} snaps
                    to the correct state without animating. Side nav toggles don't
                    change hasBreadcrumbs, so the element stays mounted and animates. */}
                <m.div
                  key={String(hasBreadcrumbs)}
                  className={styles.spacerSeparator}
                  initial={false}
                  animate={variantName}
                  custom={hasBreadcrumbs}
                  variants={spacerSeparatorVariants}
                >
                  <Text className={styles.topNavigationBreadcrumbSeparator}>/</Text>
                </m.div>
              </>
            )}

            {hasBreadcrumbs ? <Breadcrumbs breadcrumbs={plumaBreadcrumbs} flexGrow="1" flexShrink="1" /> : null}
          </InlineStack>

          <Box display="flex" alignItems="center" gap="075">
            {Object.values(AppBundle).map((bundle) => (
              <div
                key={bundle}
                className={styles.portal}
                id={`hydra-top-nav-portal-left-${bundle}`}
                style={{ ...(activeApp !== bundle ? { display: 'none' } : {}) }}
              />
            ))}
          </Box>

          <SampleWorkspaceBadge />
        </InlineStack>

        <Box display="flex" alignItems="center" justifyContent="stretch">
          <CommandDialogButton />
        </Box>

        <InlineStack alignBlock="center" alignInline="end" gap="075" whiteSpace="nowrap">
          {Object.values(AppBundle).map((bundle) => (
            <div
              key={bundle}
              className={styles.portal}
              id={`hydra-top-nav-portal-right-${bundle}`}
              style={{ ...(activeApp !== bundle ? { display: 'none' } : {}) }}
            />
          ))}
          {/* Keep the button behind the flag for now */}
          <HelpButton />
          <WorkspaceHealthNotification />
          {hasFeatureFlag(FeatureFlag.NotificationsUx) ? <NotificationsDrawer /> : <NotificationsDropdown />}
          <Copilot />
          {hasFeatureFlag(FeatureFlag.AiEnabled) ? (
            <GlobalCopilotConfiguration />
          ) : supportChat.isHandoverAllowed && supportChat.zendeskChatEnabled ? (
            <SupportChat />
          ) : null}
          <PersonDropdown />
        </InlineStack>
      </Box>
    </MotionConfig>
  );
}
